import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import AccessDenied from "./ui/AccessDenied";
import useRoleCheck from "../auth/useRoleCheck";

/*React-Bootstrap components */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import {
  useDeleteTender,
  useGetTendersPagination,
} from "../services/Tenders/tendersAPI";
import { useExportCsv } from "../services/Shared/sharedAPI";
import DataGrid from "./dataGrid/DataGrid";
import DeleteModal from "./ui/DeleteModal";
import formatDateHelper from "./utils/dateFormat";

const TENDERS_V2_GRID_STATE_KEY = "tendersV2GridState";

const TendersV2 = () => {
  const { isEditor } = useRoleCheck(MODULES.RAZPISI);

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [isGridStateHydrated, setIsGridStateHydrated] = useState(false);

  useEffect(() => {
    const rawState = sessionStorage.getItem(TENDERS_V2_GRID_STATE_KEY);
    if (!rawState) {
      setIsGridStateHydrated(true);
      return;
    }

    try {
      const parsedState = JSON.parse(rawState);
      setSorting(
        Array.isArray(parsedState?.sorting) ? parsedState.sorting : [],
      );
      setGlobalSearch(
        typeof parsedState?.globalSearch === "string"
          ? parsedState.globalSearch
          : "",
      );
      setColumnFilters(
        Array.isArray(parsedState?.columnFilters)
          ? parsedState.columnFilters
          : [],
      );
    } catch {
      setSorting([]);
      setGlobalSearch("");
      setColumnFilters([]);
    }

    setIsGridStateHydrated(true);
  }, []);

  useEffect(() => {
    if (!isGridStateHydrated) return;

    sessionStorage.setItem(
      TENDERS_V2_GRID_STATE_KEY,
      JSON.stringify({
        sorting,
        globalSearch,
        columnFilters,
      }),
    );
  }, [sorting, globalSearch, columnFilters, isGridStateHydrated]);

  useEffect(() => {
    // Reset page index to 0 when search or sorting changes
    if (!isGridStateHydrated) return;

    setPagination((old) => ({
      ...old,
      pageIndex: 0,
    }));
  }, [globalSearch, sorting, columnFilters, isGridStateHydrated]);

  // For navigation to tender details
  const navigate = useNavigate();

  // Fetch paginated tenders based on current pagination, sorting, search and filters
  const { data: tenders, isLoading } = useGetTendersPagination(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
  );

  //Fetch filters (šifrants) for the data grid (dropdowns)
  const filters = tenders?.filters;

  const prijavaDatumFilterOptions = React.useMemo(() => {
    const directCandidates = [
      filters?.prijava_datum,
      filters?.prijavaDatum,
      filters?.prijavaDatumi,
      filters?.datumPrijave,
      filters?.datumiPrijave,
    ];

    const directMatch = directCandidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length > 0,
    );

    const fallbackMatch = Object.entries(filters ?? {}).find(
      ([key, value]) =>
        /prijava/i.test(key) && Array.isArray(value) && value.length > 0,
    )?.[1];

    const sourceOptions = directMatch || fallbackMatch || [];

    return sourceOptions.map((option) => {
      if (
        option &&
        typeof option === "object" &&
        "value" in option &&
        "label" in option
      ) {
        return option;
      }

      if (option === "2100-01-01") {
        return { value: option, label: "Odprt razpis" };
      }

      return {
        value: option,
        label: option ? formatDateHelper(option) : "Brez datuma",
      };
    });
  }, [filters]);

  // Export CSV mutation
  const exportMutation = useExportCsv(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
    "Razpisi/seznamRazpisovPaginationV2",
  );

  // DELETE tender
  const {
    mutate: deleteTender,
    isPending: isDeletePending,
    isError: isDeleteError,
    error: deleteError,
    reset: resetDeleteError,
  } = useDeleteTender();

  //Set up columns for the data grid, including filters(šifrants) options for dropdown filters
  const columns = React.useMemo(
    () => [
      {
        header: "Naziv",
        accessorKey: "razpis_naziv",
      },
      {
        header: "Izpostavljen",
        accessorKey: "pomemben",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: [
            { value: true, label: "Da" },
            { value: false, label: "Ne" },
          ],
        },
        cell: (info) => {
          const value = info.getValue();
          return value ? "Da" : "Ne";
        },
      },
      {
        header: "Odprt razpis",
        accessorKey: "odprt",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: [
            { value: true, label: "Da" },
            { value: false, label: "Ne" },
          ],
        },
        cell: (info) => {
          const value = info.getValue();
          return value ? "Da" : "Ne";
        },
      },
      {
        header: "Program",
        accessorKey: "program_naziv",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.program || [],
        },
      },
      {
        header: "Nadrejeni program",
        accessorKey: "programParent",
      },
      {
        header: "Datum prijave",
        accessorKey: "prijava_datum",
        sortDescFirst: false,
        enableSortingRemoval: false,
        enableColumnFilter: true,
        meta: {
          filterVariant:
            prijavaDatumFilterOptions.length > 0
              ? "checkboxFilter"
              : "dateRangeFilter",
          filterOptions: prijavaDatumFilterOptions,
        },
        cell: (info) => {
          const value = info.getValue();
          if (!value || value === "2100-01-01") return "";

          return formatDateHelper(value);
        },
      },
    ],
    [filters?.program, prijavaDatumFilterOptions],
  );

  //handle navigate by tender ID
  const handlePodrobnostiClick = (row) => {
    navigate(`/RazpisPodrobnosti/${row.original.razpisID}`);
  };

  const [tenderToDelete, setTenderToDelete] = useState(null);

  const handleDeleteClick = (row) => {
    resetDeleteError();
    setTenderToDelete(row.original);
  };

  const handleConfirmDelete = () => {
    if (!tenderToDelete?.razpisID || isDeletePending) return;

    deleteTender(tenderToDelete.razpisID, {
      onSuccess: () => {
        resetDeleteError();
        setTenderToDelete(null);
      },
      onError: () => {
        setTimeout(() => {
          resetDeleteError();
        }, 5000);
      },
    });
  };

  return (
    <Container fluid>
      <RoleGuard
        moduleKey={MODULES.RAZPISI}
        roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
        fallback={<AccessDenied />}
      >
        <>
          <Row>
            <Col className="main-header font-weight-bold">Razpisi</Col>
          </Row>
          <Row>
            <Col className="header-divider">
              <hr />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <div className="float-end">
                <RoleGuard
                  moduleKey={MODULES.RAZPISI}
                  roles={ROLE_GROUPS.EDITORS}
                >
                  <Link
                    className="programs-button-add-text"
                    to="/Razpisi/DodajRazpis/"
                  >
                    <Button variant="outline-primary" size="sm">
                      Dodaj razpis
                    </Button>
                  </Link>
                </RoleGuard>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <DataGrid
                data={tenders?.data || []}
                columns={columns}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                sorting={sorting}
                pagination={pagination}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                pageCount={tenders?.totalPages || 0}
                loading={isLoading}
                searchGlobalPlaceholder="Iskanje po nazivu, programu..."
                globalSearch={globalSearch}
                hasGlobalSearch={true}
                onGlobalSearchChange={setGlobalSearch}
                hasActionColumn={true}
                onPodrobnostiActionClick={handlePodrobnostiClick}
                onDeleteActionClick={handleDeleteClick}
                canDelete={() => isEditor}
                onPovezavaActionClick={(row) => row.original.povezava}
                hasExportCsvButton={true}
                exportFileMutation={exportMutation}
              />
            </Col>
          </Row>
        </>
      </RoleGuard>

      <DeleteModal
        show={!!tenderToDelete}
        onHide={() => {
          setTenderToDelete(null);
          resetDeleteError();
        }}
        onConfirm={handleConfirmDelete}
        text="Ali ste prepričani, da želite izbrisati razpis:"
        naziv={tenderToDelete?.razpis_naziv}
        isError={isDeleteError}
        errorMessage={"Napaka pri brisanju. Prosimo, poskusite znova."}
        isPending={isDeletePending}
      />
    </Container>
  );
};

export default TendersV2;
